/**
 * PersonOverviewHub — archive at a glance and one recommended next step.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  BookOpen,
  FileText,
  Flame,
  Mic,
  Users,
} from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';
import { getMemorialStatusLabel, isMemorialActive } from '../../utils/memorial';
import { isFirstChapterProfile, getProfileIntention, getProfileIntentionLabel } from '../../utils/profileIntention';
import { CORE_JOURNEY_UI } from '../../config/testerRelease';
import { PersonCoreJourneyActions } from './PersonCoreJourneyActions';
import { getApiBase } from '../../utils/apiBase';
import {
  buildCompletionSuggestions,
  getPersonDisplayName,
  resolveInsightAction,
} from '../../utils/personArchiveInsight';
import axios from 'axios';

const API = getApiBase();

function GlanceStat({ icon: Icon, label, value, sublabel, onClick, testId }) {
  const content = (
    <>
      <div className="flex items-center gap-2">
        {Icon ? <Icon className="h-3.5 w-3.5 shrink-0 text-[#8b7355]/70" aria-hidden /> : null}
        <p className="text-[10px] font-mono uppercase tracking-[0.14em] text-muted-foreground/55">{label}</p>
      </div>
      <p className="mt-2 font-serif text-xl tabular-nums text-[hsl(24_12%_18%)]">{value}</p>
      {sublabel ? <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground/75">{sublabel}</p> : null}
    </>
  );

  const className = cn(
    'rounded-xl border border-[hsl(38_14%_86%)]/55 bg-[hsl(42_28%_99%)]/75 px-4 py-4 text-left transition',
    onClick && 'hover:border-[hsl(38_18%_78%)]/65 hover:bg-white/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B7355]/25',
  );

  if (typeof onClick === 'function') {
    return (
      <button type="button" onClick={onClick} data-testid={testId} className={className}>
        {content}
      </button>
    );
  }

  return (
    <div data-testid={testId} className={className}>
      {content}
    </div>
  );
}

export function PersonOverviewHub({
  person,
  storyCount = 0,
  voiceMemoriesCount = 0,
  documentCount = 0,
  possessionCount = 0,
  familyConnectionCount = 0,
  canEdit = false,
  isGenealogical = true,
  onNavigate,
}) {
  const [memorialRecord, setMemorialRecord] = useState(null);

  const name = getPersonDisplayName(person);
  const firstChapter = isFirstChapterProfile(person);

  useEffect(() => {
    if (!person?.id || !isGenealogical) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/memorials-api/by-person/${person.id}`);
        if (!cancelled) setMemorialRecord(data?.memorial || null);
      } catch {
        if (!cancelled) setMemorialRecord(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [person?.id, isGenealogical, person?.memorial_summary?.memorial_active]);

  const memorialLabel = isMemorialActive(person)
    ? getMemorialStatusLabel(memorialRecord)
    : 'Not set up';

  const ctx = useMemo(
    () => ({
      person,
      storyCount,
      voiceMemoriesCount,
      possessionCount,
      familyConnectionCount,
      memorial: memorialRecord,
      isGenealogical,
      canEdit,
    }),
    [person, storyCount, voiceMemoriesCount, possessionCount, familyConnectionCount, memorialRecord, isGenealogical, canEdit],
  );

  const nextAction = useMemo(() => {
    const suggestions = buildCompletionSuggestions(ctx);
    return suggestions[0] || null;
  }, [ctx]);

  const handlers = useMemo(
    () => ({
      onNavigate,
      onOpenMemorial: () => onNavigate?.('memorial'),
    }),
    [onNavigate],
  );

  const scrollToMoreDetails = () => {
    document.querySelector('[data-testid="person-overview-more-details"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const scrollToOutputs = () => {
    document.querySelector('[data-testid="person-overview-outputs"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="space-y-6" data-testid="person-overview-hub">
      {CORE_JOURNEY_UI && canEdit ? (
        <PersonCoreJourneyActions person={person} onNavigate={onNavigate} />
      ) : null}
      <div>
        <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground/55 mb-1">
          Archive at a glance
          {firstChapter ? (
            <span className="ml-2 inline-flex rounded-full border border-[#c9a961]/35 bg-[#c9a961]/10 px-2 py-0.5 text-[9px] font-semibold normal-case tracking-wide text-[#8b7355]">
              {getProfileIntentionLabel(getProfileIntention(person))}
            </span>
          ) : null}
        </p>
        <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground/85">
          {CORE_JOURNEY_UI
            ? `What you have preserved for ${name} — ready to grow into your family's Legacy Book.`
            : `What is preserved for ${name} — stories, voice, documents, and family connections in one calm view.`}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <GlanceStat
          icon={BookOpen}
          label="Stories"
          value={storyCount}
          sublabel={storyCount === 1 ? 'preserved story' : storyCount ? 'preserved stories' : 'none yet'}
          onClick={() => onNavigate?.('stories')}
          testId="overview-count-stories"
        />
        <GlanceStat
          icon={Mic}
          label="Voice"
          value={voiceMemoriesCount}
          sublabel={voiceMemoriesCount === 1 ? 'memory recorded' : voiceMemoriesCount ? 'memories recorded' : 'none yet'}
          onClick={() => onNavigate?.('stories', { focus: 'voice' })}
          testId="overview-count-voice"
        />
        <GlanceStat
          icon={FileText}
          label="Documents"
          value={documentCount}
          sublabel={documentCount === 1 ? 'file preserved' : documentCount ? 'files preserved' : 'none yet'}
          onClick={() => onNavigate?.('documents')}
          testId="overview-count-documents"
        />
        <GlanceStat
          icon={Users}
          label="Family"
          value={familyConnectionCount}
          sublabel={familyConnectionCount === 1 ? 'connection' : 'connections'}
          onClick={() => onNavigate?.('family')}
          testId="overview-count-family"
        />
        {isGenealogical && !CORE_JOURNEY_UI ? (
          <GlanceStat
            icon={Flame}
            label="Memorial"
            value={memorialLabel}
            sublabel={isMemorialActive(person) ? 'Memorial mode' : 'Not set up'}
            onClick={() => onNavigate?.('memorial')}
            testId="overview-count-memorial"
          />
        ) : null}
        {!CORE_JOURNEY_UI ? (
        <GlanceStat
          label="Keepsakes"
          value={possessionCount}
          sublabel={
            possessionCount === 1 ? 'linked keepsake' : possessionCount ? 'linked keepsakes' : 'see more details'
          }
          onClick={scrollToMoreDetails}
          testId="overview-count-possessions"
        />
        ) : null}
      </div>

      {canEdit && nextAction && !CORE_JOURNEY_UI ? (
        <section
          className="rounded-2xl border border-[hsl(38_14%_86%)]/60 bg-[hsl(42_28%_99%)]/85 px-5 py-5"
          data-testid="overview-recommended-next"
        >
          <p className="text-[10px] font-mono uppercase tracking-[0.16em] text-[#8b7355]">Recommended next step</p>
          <p className="mt-2 max-w-xl font-serif text-base leading-relaxed text-[hsl(24_12%_22%)]">
            One small addition makes this profile feel more like {name}.
          </p>
          <Button
            type="button"
            size="sm"
            className="mt-4 rounded-full"
            onClick={() => resolveInsightAction(nextAction.action, handlers)}
            data-testid="overview-recommended-next-btn"
          >
            {nextAction.label}
          </Button>
          <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <button
              type="button"
              className="underline-offset-2 hover:text-foreground/85 hover:underline"
              onClick={() => onNavigate?.('stories')}
            >
              Browse stories &amp; voice
            </button>
            <button
              type="button"
              className="underline-offset-2 hover:text-foreground/85 hover:underline"
              onClick={() => onNavigate?.('documents')}
            >
              Add a document
            </button>
            {isGenealogical ? (
              <button
                type="button"
                className="underline-offset-2 hover:text-foreground/85 hover:underline"
                onClick={scrollToOutputs}
              >
                View outputs &amp; sharing
              </button>
            ) : null}
          </div>
        </section>
      ) : null}
    </div>
  );
}

export default PersonOverviewHub;
